import { useEffect, useMemo, useState } from 'react';
import { Check, Search, X } from 'lucide-react';
import AdminShell from '../../components/AdminShell.jsx';
import Alert from '../../components/Alert.jsx';
import AnimatedCounter from '../../components/AnimatedCounter.jsx';
import Loader from '../../components/Loader.jsx';
import { api } from '../../api/client.js';
import { useAdminQueues } from '../../context/AdminQueueContext.jsx';

// /admin/access-requests — queue of overlay-access requests waiting on a
// decision. Approve or deny per-row; approving inserts into user_overlays
// (idempotent) and marks the request approved.
//
// All three status lists are fetched up front (they're small) so the filter
// pills carry counts, the header stats are real, and switching tabs or
// searching never round-trips.

const STATUSES = ['pending', 'approved', 'denied'];

const COLS = {
  '--adm-cols': 'minmax(200px, 1.1fr) 190px 230px 210px 230px 130px',
  '--adm-minw': '1214px',
};

export default function AdminAccessRequests() {
  const { refresh: refreshQueues } = useAdminQueues();
  const [status, setStatus] = useState('pending');
  const [query, setQuery] = useState('');
  const [requests, setRequests] = useState(null); // all statuses, one list
  const [error, setError] = useState(null);
  const [decidingId, setDecidingId] = useState(null);
  const [note, setNote] = useState(null);

  function load() {
    return Promise.all(STATUSES.map((s) => api.adminListAccessRequests(s)))
      .then((results) => setRequests(results.flatMap((r) => r?.requests ?? [])))
      .catch((e) => setError(e.message));
  }

  useEffect(() => {
    let alive = true;
    Promise.all(STATUSES.map((s) => api.adminListAccessRequests(s)))
      .then((results) => { if (alive) setRequests(results.flatMap((r) => r?.requests ?? [])); })
      .catch((e) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!note) return;
    const t = setTimeout(() => setNote(null), 3500);
    return () => clearTimeout(t);
  }, [note]);

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, denied: 0 };
    for (const r of requests ?? []) c[r.status] = (c[r.status] ?? 0) + 1;
    return c;
  }, [requests]);

  const visible = useMemo(() => {
    let list = (requests ?? []).filter((r) => r.status === status);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((r) =>
        `${r.requester?.username ?? ''} ${r.package?.name ?? ''} ${r.overlay?.name ?? ''}`
          .toLowerCase()
          .includes(q));
    }
    return list;
  }, [requests, status, query]);

  async function decide(id, decision, label) {
    setDecidingId(id);
    setError(null);
    try {
      await api.adminDecideAccessRequest(id, decision, null);
      setNote(`Request ${label}.`);
      // Re-fetch rather than patching locally: the server stamps decidedAt/By.
      await load();
      refreshQueues();
    } catch (e) {
      setError(e.message);
    } finally {
      setDecidingId(null);
    }
  }

  return (
    <AdminShell crumb="Access Requests">
      <header className="adm-head adm-head--split adm-rv" style={{ animationDelay: '80ms' }}>
        <div>
          <h1 className="adm-head__title">Access Requests</h1>
          <p className="adm-head__sub">
            Overlay-access requests from tournament organizers. Approving inserts the grant
            and marks the request approved.
          </p>
        </div>
        <div className="adm-stats">
          <div className="adm-stat adm-stat--amber">
            <div className="adm-stat__value"><AnimatedCounter to={counts.pending} pad={2} duration={1200} /></div>
            <div className="adm-stat__label">Pending</div>
          </div>
          <span className="adm-stats__sep" aria-hidden="true" />
          <div className="adm-stat adm-stat--ok">
            <div className="adm-stat__value"><AnimatedCounter to={counts.approved} pad={2} duration={1200} /></div>
            <div className="adm-stat__label">Approved</div>
          </div>
          <span className="adm-stats__sep" aria-hidden="true" />
          <div className="adm-stat adm-stat--pink">
            <div className="adm-stat__value"><AnimatedCounter to={counts.denied} pad={2} duration={1200} /></div>
            <div className="adm-stat__label">Denied</div>
          </div>
        </div>
      </header>

      {error && (
        <Alert kind="danger" onClose={() => setError(null)}>{error}</Alert>
      )}

      <div className="adm-filterbar adm-rv" style={{ animationDelay: '140ms' }}>
        <div className="adm-search">
          <Search size={16} aria-hidden="true" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search user, package, overlay…"
            aria-label="Search access requests"
          />
        </div>
        <div className="adm-pills" role="group" aria-label="Filter by status">
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              className={`adm-pill${s === status ? ' is-active' : ''}`}
              onClick={() => setStatus(s)}
              aria-pressed={s === status}
            >
              {s} <span className="adm-pill__count">{counts[s]}</span>
            </button>
          ))}
        </div>
        <span className="adm-shown">{visible.length} of {(requests ?? []).length} shown</span>
      </div>

      {note && (
        <div className="adm-note" role="status">
          <Check size={14} aria-hidden="true" />
          {note}
        </div>
      )}

      {requests === null ? (
        <Loader label="Loading…" />
      ) : (
        <div className="adm-scroll adm-scroll--cards adm-rv" style={{ ...COLS, animationDelay: '220ms' }}>
          <div className="adm-grid__head">
            <span>User</span>
            <span>Package</span>
            <span>Overlay</span>
            <span>Requested</span>
            <span>Decided</span>
            <span className="is-right">Actions</span>
          </div>
          <div className="adm-grid__rule" aria-hidden="true" />
          {visible.map((r, i) => (
            <div
              className="adm-grid__row adm-rvl"
              key={r.id}
              style={{ animationDelay: `${90 + i * 45}ms` }}
            >
              <div className="adm-ident" style={{ gap: 14 }}>
                <span className="adm-initial" aria-hidden="true">
                  {r.requester?.username?.[0]?.toUpperCase() ?? '?'}
                </span>
                <span className="adm-ident__name">
                  {r.requester?.username ?? <span className="adm-cell--faint">(deleted)</span>}
                </span>
              </div>
              {/* display:contents on desktop; the card's chip line under the
                  requester on phones (same pattern as Users/Grants). */}
              <div className="adm-grid__meta">
                <div className="adm-cell">{r.package?.name ?? '—'}</div>
                <div className="adm-cell adm-cell--main" style={{ fontSize: 15 }}>
                  {r.overlay?.name ?? <span className="adm-cell--faint">(removed)</span>}
                </div>
                <div className="adm-cell adm-cell--dim" data-mlabel="requested">
                  {new Date(r.requestedAt).toLocaleString()}
                </div>
                <div
                  className="adm-cell adm-cell--dim"
                  data-mlabel={r.decidedAt ? 'decided' : undefined}
                >
                  {r.decidedAt ? (
                    <span className="adm-cell adm-cell--dim">
                      {new Date(r.decidedAt).toLocaleString()}
                      {r.decidedBy?.username && (
                        <span className="adm-cell__sub">by {r.decidedBy.username}</span>
                      )}
                    </span>
                  ) : (
                    <span className="adm-cell adm-cell--faint">—</span>
                  )}
                </div>
              </div>
              <div className="adm-grid__actions">
                {r.status === 'pending' ? (
                  <>
                    <button
                      type="button"
                      className="btn-icon btn-icon--success"
                      disabled={decidingId === r.id}
                      onClick={() => decide(r.id, 'approve', 'approved — grant inserted')}
                      title="Approve request"
                      aria-label="Approve request"
                    >
                      <Check size={17} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="btn-icon btn-icon--danger"
                      disabled={decidingId === r.id}
                      onClick={() => decide(r.id, 'deny', 'denied')}
                      title="Deny request"
                      aria-label="Deny request"
                    >
                      <X size={17} aria-hidden="true" />
                    </button>
                  </>
                ) : r.status === 'approved' ? (
                  <span className="adm-chip adm-chip--ok">Approved</span>
                ) : (
                  <span className="adm-chip adm-chip--bad">Denied</span>
                )}
              </div>
            </div>
          ))}
          {visible.length === 0 && (
            <div className="adm-empty">
              {query.trim() ? 'No requests match your search.' : `No ${status} requests.`}
            </div>
          )}
        </div>
      )}
    </AdminShell>
  );
}
